import React, { useState, useEffect, useRef } from "react";
import { Eye, EyeOff, X, Plus, Search } from "lucide-react";
import UserAvatar from "components/UserAvatar";

function nameOf(u = {}) {
  return (
    u.name ||
    u.displayName ||
    [u.firstName, u.lastName].filter(Boolean).join(" ") ||
    u.email ||
    "User"
  );
}

/**
 * WatchersPanel (SOUND-526) — ticket workspace right-rail. A Watch/Watching
 * self-toggle for the current user, the watcher list (avatars + names), and an
 * internal-user picker to add/remove other watchers. Internal-only (watchers are
 * a Sound-ops concern). Mutations are page-owned (optimistic + rollback).
 */
export default function WatchersPanel({
  watchers = [],
  currentUserId,
  directory = [],
  onWatchSelf,
  onAddWatcher,
  onRemoveWatcher,
}) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  const isWatching = watchers.some((w) => w.userId === currentUserId);

  useEffect(() => {
    if (!adding) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAdding(false);
    };
    const onKey = (e) => e.key === "Escape" && setAdding(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [adding]);

  const watchingIds = new Set(watchers.map((w) => w.userId));
  const q = query.trim().toLowerCase();
  const candidates = directory
    .filter((u) => !watchingIds.has(u.id || u._id))
    .filter((u) => !q || nameOf(u).toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
    .slice(0, 8);

  return (
    <section className="surface p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Eye className="h-3.5 w-3.5" />
          Watchers
          {watchers.length > 0 && <span className="text-muted-foreground/70">· {watchers.length}</span>}
        </h3>
        <button
          type="button"
          onClick={() => (isWatching ? onRemoveWatcher?.(currentUserId) : onWatchSelf?.())}
          className={`btn h-7 px-2.5 text-xs ${isWatching ? "btn-secondary" : "btn-outline"}`}
        >
          {isWatching ? (
            <>
              <EyeOff className="h-3.5 w-3.5" />
              Watching
            </>
          ) : (
            <>
              <Eye className="h-3.5 w-3.5" />
              Watch
            </>
          )}
        </button>
      </div>

      {watchers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No watchers yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {watchers.map((w) => (
            <li key={w.userId} className="group flex items-center gap-2">
              <UserAvatar user={{ name: w.name, email: w.email }} />
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                {w.userId === currentUserId ? "You" : w.name || w.email || w.userId}
              </span>
              <button
                type="button"
                onClick={() => onRemoveWatcher?.(w.userId)}
                className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                title="Remove watcher"
                aria-label="Remove watcher"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative mt-2" ref={ref}>
        {adding ? (
          <div className="rounded-md border border-border bg-muted/20 p-2">
            <div className="relative mb-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                className="input h-8 pl-8 text-sm"
                placeholder="Search team…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {candidates.length === 0 ? (
                <div className="py-2 text-center text-xs text-muted-foreground">
                  {q ? "No matches." : "Everyone's watching."}
                </div>
              ) : (
                candidates.map((u) => (
                  <button
                    key={u.id || u._id}
                    type="button"
                    onClick={() => {
                      onAddWatcher?.(u.id || u._id);
                      setAdding(false);
                      setQuery("");
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted"
                  >
                    <UserAvatar user={u} />
                    <span className="min-w-0 flex-1 truncate text-sm">{nameOf(u)}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="btn btn-ghost h-7 px-2 text-xs text-muted-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            Add watcher
          </button>
        )}
      </div>
    </section>
  );
}
